import { Form, FormInstance, Modal } from 'antd'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useDiscountSchemas } from 'hooks/useDiscountSchemas'
import { DiscountSchema } from 'models/discount'
import { useCallback, useState } from 'react'
import { HeadingWrapper } from '../../../atom/plans'
import { PricingSchema } from '../../../models/price'
import { Tenant } from '../../../models/tenant'
import CreateDiscountSchemaModal from './create-discount-schema-modal'
import EditDiscountSchemaSection from './discount-schema/edit-discount-schema-section'
import EditPricingSchemaSection from './edit-pricing-schema-section'
import { StepperTitle } from './stepper-title'
import { DiscountFooterSectionModal } from './discount-schema/discount-footer-section'
interface props {
  tenant: Tenant
  selectedPricingSchema: PricingSchema
  disableChargerId?: boolean
  onCancel: () => void
  onOk: () => void
  hideMaxLimit?: boolean
  discounts?: DiscountSchema[]
}

const EditPricingSchemaModal: React.FC<props> = ({
  tenant,
  disableChargerId = false,
  selectedPricingSchema,
  onCancel,
  onOk,
  hideMaxLimit = false,
  discounts,
}) => {
  const [form] = Form.useForm()
  const { pricingPlanText } = useGeneralTranslation()

  const isAbleToEditDiscountPlan = true

  const afterClose = () => form.resetFields()

  const [current, setCurrent] = useState(0)
  const [loadedPricingSchema, setLoadedPricingSchema] = useState(selectedPricingSchema)
  const [selectedDiscountSchemas, setSelectedDiscountSchemas] = useState<DiscountSchema[]>([])
  const { convertPricingSchemaToPayload } = useDiscountSchemas(
    tenant,
    () => {},
    () => {},
  )

  const [discountForms, setDiscountForms] = useState<FormInstance[]>([])

  const [multiDiscountNameExists, setMultiDiscountNameExists] = useState({})
  const [multiPromoCodeExists, setMultiPromoCodeExists] = useState({})

  const handleFormRef = useCallback((form: FormInstance) => {
    setDiscountForms((prev) => {
      if (prev.some((f) => f === form)) {
        return prev
      }
      return [...prev, form]
    })
  }, [])

  const handleSubmitAllForms = async () => {
    try {
      await Promise.all(discountForms.map((form) => form.submit()))
    } catch (error) {
      console.error('Form validation failed:', error)
    }
  }

  const onEditDiscountSchemaFinish = (goToCreate?: boolean) => {
    if (!!selectedDiscountSchemas.length && isAbleToEditDiscountPlan) {
      setCurrent(1)
    } else if (goToCreate) {
      setCurrent(3)
    } else {
      onOk()
    }
  }

  const goBack = () => setCurrent(0)

  const closeModal = () => {
    if (current === 0) {
      onCancel()
    } else {
      onOk()
    }
  }

  return (
    <Modal
      title={
        (!!selectedDiscountSchemas.length && isAbleToEditDiscountPlan) || current === 3 ? (
          <StepperTitle current={current % 2} />
        ) : (
          <HeadingWrapper>{pricingPlanText}</HeadingWrapper>
        )
      }
      visible={true}
      onCancel={closeModal}
      width={1034}
      afterClose={afterClose}
      footer={null}
      destroyOnClose
      style={{ top: 20 }}
    >
      {current === 0 ? (
        <EditPricingSchemaSection
          tenant={tenant}
          selectedPricingSchema={loadedPricingSchema}
          setLoadedPricingSchema={setLoadedPricingSchema}
          disableChargerId={disableChargerId}
          onCancel={onCancel}
          onOk={onEditDiscountSchemaFinish}
          hideMaxLimit={hideMaxLimit}
          selectedDiscountSchemas={selectedDiscountSchemas}
          setSelectedDiscountSchemas={setSelectedDiscountSchemas}
          discounts={discounts}
        />
      ) : current === 1 ? (
        <>
          {selectedDiscountSchemas.map((discount, index) => (
            <EditDiscountSchemaSection
              tenant={tenant}
              key={discount.id}
              selectedDiscountSchema={discount}
              selectedPricingSchema={convertPricingSchemaToPayload(loadedPricingSchema, 'both')}
              discounts={discounts as DiscountSchema[]}
              discountId={discount.id as number}
              onCancel={onCancel}
              onOk={onOk}
              goBack={goBack}
              handleSubmitAllForms={handleSubmitAllForms}
              index={index + 1}
              lastIndex={selectedDiscountSchemas.length}
              formRef={handleFormRef}
              forms={discountForms}
              setMultiDiscountNameExists={setMultiDiscountNameExists}
              setMultiPromoCodeExists={setMultiPromoCodeExists}
            />
          ))}
          {selectedDiscountSchemas.length > 1 && (
            <DiscountFooterSectionModal
              loading={false}
              form={form}
              discountNameExists={multiDiscountNameExists}
              promoCodeExists={multiPromoCodeExists}
              current={1}
              modalType={'discount-only'}
              onCancel={onCancel}
              back={goBack}
              forms={discountForms}
              multiDiscountNameExists={multiDiscountNameExists}
              multiPromoCodeExists={multiPromoCodeExists}
              handleSubmitAllForms={handleSubmitAllForms}
            />
          )}
        </>
      ) : (
        <CreateDiscountSchemaModal
          tenant={tenant}
          listings={convertPricingSchemaToPayload(loadedPricingSchema, 'discount-only')?.applied_listings || []}
          pricingSchemaValues={convertPricingSchemaToPayload(loadedPricingSchema, 'discount-only')}
          discounts={discounts || []}
          modalType="edit"
          onCancel={onCancel}
          onOk={onOk}
          back={goBack}
        />
      )}
    </Modal>
  )
}

export default EditPricingSchemaModal
